import React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, IMInputText } from '@insurancemenu/imenu-components-lib';
import { IMSicSearch } from '@insurancemenu/imenu-components-lib/dist/components/imForm/capabilities/sic/IMSicSearch';
import SectionTitle from 'components/common/general/SectionTitle';
import DomainProductLines from 'components/common/initiate-proposal/DomainProductLines';
import {
  FollowingSectionTitleStyled,
  IMZipCodeFieldStyled,
  IMZipCountyFieldStyled,
  IMZipStateFieldStyled,
  TabIMFormStyled,
  FormFieldWrapperStyled,
  AlertStyled,
} from 'components/common/initiate-proposal/styles';
import IMEffectiveDateSelect from 'components/common/form/IMEffectiveDateSelect';
import BasicInfoStepController from 'store/pages/initiate-quote/sunLife/basicInfoStepController';
import { INCORPORATION_DATE_GUIDELINES } from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import {
  SUN_LIFE_INCORPORATION_DATE_ALERT_TEST_ID,
  SUN_LIFE_INITIATE_PROPOSAL_FORM_ID,
  SUN_LIFE_SIC_WARNING_ALERT_TEST_ID,
} from './constants';
import { IncorporationDateAlertStyled } from './styles';

interface Props {
  basicInfoStepController: BasicInfoStepController;
}

const SunLifeInitiateProposalForm: React.FC<Props> = observer(({ basicInfoStepController }) => {
  const { form, effectiveDatesConfig, domainProductLineItems, isDomainProductLinesLoading, sicWarningMessage } =
    basicInfoStepController;

  const showWarning = Boolean(sicWarningMessage);

  return (
    <TabIMFormStyled id={SUN_LIFE_INITIATE_PROPOSAL_FORM_ID} formController={form}>
      <SectionTitle title="Basic Information" />
      <IMInputText label="Proposal Name" name="proposalName" size="full" />
      <IMEffectiveDateSelect label="Effective Date" name="effectiveDate" size="full" options={effectiveDatesConfig} />
      <IMZipCodeFieldStyled size="s" />
      <IMZipStateFieldStyled size="s" />
      <IMZipCountyFieldStyled size="s" />

      <FollowingSectionTitleStyled title="Additional Information" />
      <IncorporationDateAlertStyled
        content={[
          'Group must have been in business for a minimum of 12 months prior to the requested policy effective date.',
        ]}
        title="Note"
        type="warning"
        data-test-id={SUN_LIFE_INCORPORATION_DATE_ALERT_TEST_ID}
      />
      <IMInputText
        label="Incorporation Date"
        name="incorporationDate"
        size="full"
        htmlType="date"
        additionalOptions={{
          guidelines: INCORPORATION_DATE_GUIDELINES,
          showGuidelinesByDefault: true,
        }}
      />
      <IMInputText label="Estimated Number of Eligible Employees" name="groupSize" size="full" />
      <FormFieldWrapperStyled>
        <IMSicSearch label="SIC Code" size="full" />
        <Collapse show={showWarning}>
          <AlertStyled
            content={[sicWarningMessage]}
            title="Note"
            type="warning"
            data-test-id={SUN_LIFE_SIC_WARNING_ALERT_TEST_ID}
          />
        </Collapse>
      </FormFieldWrapperStyled>

      <FollowingSectionTitleStyled title="Available Product Lines" />
      <DomainProductLines domainProductLineItems={domainProductLineItems} isLoading={isDomainProductLinesLoading} />
    </TabIMFormStyled>
  );
});

export default SunLifeInitiateProposalForm;
